import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Target, TrendingUp, Sparkles, Zap, Layers, Rocket } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

const VARIANTS = [
  {
    key: "baseline",
    label: "Baseline",
    sub: "Strict A-Grade",
    Icon: Target,
    iconClass: "text-mb-emerald",
    pillActive: "border-mb-emerald bg-mb-emerald/5 text-mb-emerald",
    blurb: "5-of-6 Pressure · 50/50 scale-out · TP1 +2.5% / Moon +12%",
  },
  {
    key: "volatility_embrace",
    label: "Volatility Embrace",
    sub: "Loosened entry",
    Icon: Zap,
    iconClass: "text-amber-600",
    pillActive: "border-amber-400 bg-amber-50 text-amber-700",
    blurb: "Drop volatility filter · allow extension to 12% · same exits as Baseline",
  },
  {
    key: "moon_scale_out",
    label: "Moon Scale-Out",
    sub: "3-tier exits",
    Icon: Sparkles,
    iconClass: "text-purple-600",
    pillActive: "border-purple-400 bg-purple-50 text-purple-700",
    blurb: "33/33/34 split · TP1 +2.5 / TP2 +8 / Moon +25 · 12% trail · 25d hold",
  },
  {
    key: "pre_breakout",
    label: "Pre-Breakout Index",
    sub: "Leading signals",
    Icon: Rocket,
    iconClass: "text-pink-600",
    pillActive: "border-pink-400 bg-pink-50 text-pink-700",
    blurb: "2-of-3 leading: Squeeze + OBV + RS · TP1 +6 / Moon +20 · 25d hold",
  },
];

export default function Backtest() {
  const [mode, setMode] = useState("single"); // 'single' | 'compare'
  const [variant, setVariant] = useState("baseline");
  const [data, setData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSingle = async (v) => {
    setLoading(true);
    try {
      const r = await api.get(`/backtest?variant=${v}`);
      setData(r.data);
    } catch (e) {
      console.error("backtest load failed", e);
    }
    setLoading(false);
  };

  const loadCompare = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/backtest/compare`);
      setCompareData(r.data);
    } catch (e) {
      console.error("backtest compare failed", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (mode === "single") loadSingle(variant);
    else loadCompare();
  }, [mode, variant]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="label-uppercase text-mb-emerald">Strategy backtest</div>
        <h1 className="font-display font-semibold text-4xl tracking-tight mt-1">Two strategies. One edge.</h1>
        <p className="text-neutral-600 mt-1 text-sm max-w-3xl">
          Moonbucks runs two parallel strategies on every scan. <span className="font-semibold text-mb-emerald">Steady</span> for high-hit-rate insurance,{" "}
          <span className="font-semibold text-pink-600">Moonshot</span> for the rare bigger wins. Both validated on 12 months of walk-forward data.
        </p>

        {/* What this is — what it isn't */}
        <div className="mt-4 p-4 rounded-md mb-card-elevated flex items-start gap-3" data-testid="proof-framing">
          <div className="label-uppercase text-[10px]" style={{ color: "var(--mb-emerald)", letterSpacing: "0.16em" }}>NOTE</div>
          <div className="text-xs text-neutral-600 leading-relaxed">
            These are <span className="font-semibold text-mb-ink">pure-algorithm</span> results — what the engine would publish with zero human filter.
            Every alert that lands in your feed is <span className="font-semibold text-mb-ink">also vetted by the desk</span> for sentiment, conviction, and timing — see the{" "}
            <Link to="/track-record" className="text-mb-emerald font-semibold underline-offset-2 hover:underline">live track record →</Link> for actual published-alert outcomes.
          </div>
        </div>

        {/* Public proof: Steady vs Moonshot — drawn from the compare endpoint */}
        <PublicProofSection />

        <div className="mt-12 pt-8 border-t border-mb-border">
          <div className="label-uppercase text-neutral-500">Strategy lab</div>
          <h2 className="font-display font-semibold text-2xl tracking-tight mt-1">Tuning the moonshot rate</h2>
          <p className="text-neutral-600 mt-1 text-sm max-w-3xl">
            6 variants compared on the same universe — see why these two were chosen and why the others were rejected.
          </p>
        </div>

        {/* Mode toggle */}
        <div className="mt-6 flex gap-2" data-testid="mode-toggle">
          <Button
            variant={mode === "single" ? "default" : "outline"}
            className={`rounded-md ${mode === "single" ? "bg-mb-emerald hover:bg-mb-emerald-dark" : ""}`}
            onClick={() => setMode("single")}
            data-testid="mode-single-btn"
          >
            <Layers className="w-4 h-4 mr-2" /> Single variant
          </Button>
          <Button
            variant={mode === "compare" ? "default" : "outline"}
            className={`rounded-md ${mode === "compare" ? "bg-mb-emerald hover:bg-mb-emerald-dark" : ""}`}
            onClick={() => setMode("compare")}
            data-testid="mode-compare-btn"
          >
            <TrendingUp className="w-4 h-4 mr-2" /> Compare all 6
          </Button>
        </div>

        {/* Variant selector (single mode) */}
        {mode === "single" && (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="variant-selector">
            {VARIANTS.map((v) => (
              <VariantPill
                key={v.key}
                v={v}
                active={variant === v.key}
                onClick={() => setVariant(v.key)}
              />
            ))}
          </div>
        )}

        {loading && (
          <div className="py-20 text-center" data-testid="loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-mb-emerald" />
            <div className="text-xs text-neutral-500 mt-2">
              {mode === "compare" ? "Running 6 walk-forward simulations…" : "Running walk-forward simulation…"}
            </div>
          </div>
        )}

        {!loading && mode === "single" && data && (
          <SingleView data={data} variant={variant} />
        )}

        {!loading && mode === "compare" && compareData && (
          <CompareView data={compareData} />
        )}
      </div>
    </div>
  );
}

// ───────────────── Single-variant view ─────────────────
function SingleView({ data, variant }) {
  const stats = data?.stats;
  if (!stats || !stats.total_trades) {
    return (
      <div className="py-16 text-center text-neutral-500 bg-white rounded-lg border border-mb-border mt-6" data-testid="no-trades">
        No trades found in the lookback window.
      </div>
    );
  }
  const meta = VARIANTS.find((v) => v.key === variant) || VARIANTS[0];
  const isHighWR = stats.win_rate >= 80;
  const moonRate = stats.total_trades ? Math.round((stats.moon_hits / stats.total_trades) * 100) : 0;

  return (
    <div data-testid={`single-view-${variant}`}>
      <div className="mt-6 rounded-lg bg-white border border-mb-border p-5">
        <div className="flex items-center gap-2">
          <meta.Icon className={`w-4 h-4 ${meta.iconClass}`} />
          <div className="font-display font-bold text-lg">{stats.variant_label}</div>
        </div>
        <div className="text-xs text-neutral-500 mt-1 font-mono-data">{stats.variant_tagline}</div>
      </div>

      <div className={`mt-6 rounded-lg p-8 ${isHighWR ? "bg-mb-emerald text-white" : "bg-amber-50 border border-amber-200"}`} data-testid="wr-banner">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6">
          <div>
            <div className={`label-uppercase ${isHighWR ? "text-white/60" : "text-amber-700"}`}>Historical Win Rate</div>
            <div className="font-mono-data font-extrabold text-7xl mt-1" data-testid="wr-value">{stats.win_rate}%</div>
            <p className={`mt-2 text-sm ${isHighWR ? "text-white/80" : "text-amber-800"}`}>
              {stats.total_trades} A-grade entries · TP1 hit on {stats.tp1_hit_count} of them ({Math.round((stats.tp1_hit_count / stats.total_trades) * 100)}%).
            </p>
          </div>
          <div className="flex gap-6">
            <div>
              <div className={`label-uppercase ${isHighWR ? "text-white/60" : "text-amber-700"}`}>Per-trade expectancy</div>
              <div className="font-mono-data font-extrabold text-3xl mt-1" data-testid="expectancy-value">
                {stats.expectancy_pct >= 0 ? "+" : ""}{stats.expectancy_pct}%
              </div>
            </div>
            <div>
              <div className={`label-uppercase ${isHighWR ? "text-white/60" : "text-amber-700"}`}>🚀 Moonshot rate</div>
              <div className="font-mono-data font-extrabold text-3xl mt-1" data-testid="moon-rate-value">{moonRate}%</div>
              <div className={`text-xs mt-1 ${isHighWR ? "text-white/70" : "text-amber-700"}`}>
                {stats.moon_hits} of {stats.total_trades} hit +{stats.moon_target_pct}%
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="backtest-stats">
        <StatCard label="Avg Win" val={`+${stats.avg_win_pct}%`} className="text-buy" />
        <StatCard label="Avg Loss" val={`${stats.avg_loss_pct}%`} className="text-sell" />
        <StatCard label="Best Trade" val={`+${stats.best_trade_pct}%`} highlight />
        <StatCard label="Worst Trade" val={`${stats.worst_trade_pct}%`} />
        <StatCard label="🌙 Moon Hits" val={stats.moon_hits} className="text-amber-600" />
        <StatCard label="Trail Exits" val={stats.trail_hits} />
        <StatCard label="Breakeven" val={stats.breakeven_hits} />
        <StatCard label="Stops Hit" val={stats.sl_hits} className="text-sell" />
      </div>

      <div className="mt-8 bg-white border border-mb-border rounded-lg overflow-hidden" data-testid="trades-table">
        <div className="px-6 py-4 border-b border-mb-border flex items-center justify-between">
          <div className="label-uppercase text-neutral-500">Last 30 simulated trades</div>
          <div className="font-mono-data text-xs text-neutral-500">Position-weighted return per trade</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-mb-border">
              <tr>
                <Th>Symbol</Th><Th>Entry</Th><Th>Exit</Th><Th>Days</Th>
                <Th>Entry $</Th><Th>TP1 $</Th><Th>Moon $</Th><Th>Return</Th><Th>Exit</Th>
              </tr>
            </thead>
            <tbody>
              {data.trades.map((t, i) => (
                <tr key={i} className="border-b border-neutral-100" data-testid={`trade-row-${i}`}>
                  <Td className="font-mono-data font-bold">{t.symbol}</Td>
                  <Td className="font-mono-data text-neutral-500 text-xs">{t.entry_date}</Td>
                  <Td className="font-mono-data text-neutral-500 text-xs">{t.exit_date}</Td>
                  <Td className="font-mono-data">{t.days_held}d</Td>
                  <Td className="font-mono-data">${t.entry_price}</Td>
                  <Td className="font-mono-data text-emerald-700">${t.tp1_price}</Td>
                  <Td className="font-mono-data text-amber-600">${t.tp2_price}</Td>
                  <Td className={`font-mono-data font-bold ${t.win ? "text-buy" : "text-sell"}`}>
                    {t.return_pct >= 0 ? "+" : ""}{t.return_pct}%
                  </Td>
                  <Td><ExitBadge reason={t.exit_reason} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ───────────────── Compare-all view ─────────────────
function CompareView({ data }) {
  const variants = data?.variants || [];
  // Find best per metric for highlighting
  const bestExp = Math.max(...variants.map((v) => v.stats.expectancy_pct ?? -999));
  const bestWR = Math.max(...variants.map((v) => v.stats.win_rate ?? 0));
  const bestMoonRate = Math.max(
    ...variants.map((v) =>
      v.stats.total_trades ? (v.stats.moon_hits / v.stats.total_trades) * 100 : 0
    )
  );
  const bestBest = Math.max(...variants.map((v) => v.stats.best_trade_pct ?? 0));

  return (
    <div className="mt-6" data-testid="compare-view">
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {variants.map((v) => {
          const meta = VARIANTS.find((m) => m.key === v.key) || VARIANTS[0];
          const s = v.stats;
          if (!s.total_trades) {
            return (
              <div key={v.key} className="rounded-lg bg-white border border-mb-border p-6 text-center text-neutral-500" data-testid={`compare-card-${v.key}`}>
                <meta.Icon className={`w-6 h-6 mx-auto ${meta.iconClass} mb-2`} />
                <div className="font-display font-bold">{meta.label}</div>
                <div className="text-xs mt-2">No trades</div>
              </div>
            );
          }
          const moonRate = Math.round((s.moon_hits / s.total_trades) * 100);
          return (
            <div key={v.key} className="rounded-lg bg-white border-2 border-mb-border p-6" data-testid={`compare-card-${v.key}`}>
              <div className="flex items-center gap-2">
                <meta.Icon className={`w-5 h-5 ${meta.iconClass}`} />
                <div className="font-display font-bold text-lg">{meta.label}</div>
              </div>
              <div className="text-[11px] text-neutral-500 mt-1 font-mono-data leading-snug">{s.variant_tagline}</div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <CompareMetric label="Win rate" val={`${s.win_rate}%`} best={s.win_rate === bestWR} />
                <CompareMetric label="Expectancy" val={`${s.expectancy_pct >= 0 ? "+" : ""}${s.expectancy_pct}%`} best={s.expectancy_pct === bestExp} primary />
                <CompareMetric label="Moonshot rate" val={`${moonRate}%`} best={moonRate === bestMoonRate && moonRate > 0} primary />
                <CompareMetric label="Best trade" val={`+${s.best_trade_pct}%`} best={s.best_trade_pct === bestBest} />
                <CompareMetric label="Avg win" val={`+${s.avg_win_pct}%`} />
                <CompareMetric label="Avg loss" val={`${s.avg_loss_pct}%`} />
                <CompareMetric label="Trades" val={s.total_trades} />
                <CompareMetric label="Stops hit" val={s.sl_hits} />
              </div>

              <div className="mt-5 pt-4 border-t border-neutral-100">
                <div className="label-uppercase text-neutral-500 mb-2">Exit breakdown</div>
                <ExitBar stats={s} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Verdict / takeaway */}
      <Verdict variants={variants} />
    </div>
  );
}

function Verdict({ variants }) {
  if (!variants.length) return null;
  const winner = [...variants].sort((a, b) => (b.stats.expectancy_pct ?? -999) - (a.stats.expectancy_pct ?? -999))[0];
  const meta = VARIANTS.find((m) => m.key === winner.key);
  return (
    <div className="mt-6 rounded-lg bg-mb-emerald text-white p-6" data-testid="verdict">
      <div className="label-uppercase text-white/60">Best per-trade expectancy</div>
      <div className="font-display font-semibold text-2xl mt-1">{meta?.label}</div>
      <div className="text-sm text-white/80 mt-1">
        {winner.stats.expectancy_pct >= 0 ? "+" : ""}
        {winner.stats.expectancy_pct}% per trade · {winner.stats.win_rate}% WR · {winner.stats.moon_hits} moon hits across {winner.stats.total_trades} entries
      </div>
    </div>
  );
}

function ExitBar({ stats }) {
  const total = stats.total_trades || 1;
  const segs = [
    { key: "moon", count: stats.moon_hits, color: "bg-amber-500", label: "Moon" },
    { key: "trail", count: stats.trail_hits, color: "bg-emerald-500", label: "Trail" },
    { key: "be", count: stats.breakeven_hits, color: "bg-blue-400", label: "BE" },
    { key: "time", count: stats.time_exits, color: "bg-neutral-400", label: "Time" },
    { key: "sl", count: stats.sl_hits, color: "bg-red-500", label: "Stop" },
  ];
  return (
    <>
      <div className="h-3 w-full rounded-md overflow-hidden flex bg-neutral-100">
        {segs.map((s) => {
          const pct = (s.count / total) * 100;
          if (pct === 0) return null;
          return <div key={s.key} className={s.color} style={{ width: `${pct}%` }} title={`${s.label}: ${s.count}`} />;
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono-data text-neutral-600">
        {segs.map((s) => (
          <span key={s.key}>
            <span className={`inline-block w-2 h-2 rounded-full ${s.color} mr-1 align-middle`} />
            {s.label} {s.count}
          </span>
        ))}
      </div>
    </>
  );
}

const CompareMetric = ({ label, val, best, primary }) => (
  <div className={`rounded-lg p-2.5 ${best ? "bg-mb-emerald/10 ring-1 ring-mb-emerald/40" : "bg-neutral-50"}`}>
    <div className="label-uppercase text-neutral-500 text-[9px]">{label}</div>
    <div className={`font-mono-data font-extrabold ${primary ? "text-xl" : "text-base"} ${best ? "text-mb-emerald" : ""}`}>
      {val}
    </div>
  </div>
);

const VariantPill = ({ v, active, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg p-4 border-2 text-left transition-all ${active ? v.pillActive : "border-mb-border bg-white hover:border-neutral-300"}`}
      data-testid={`variant-pill-${v.key}`}
    >
      <div className="flex items-center gap-2">
        <v.Icon className="w-4 h-4" />
        <div className="font-display font-bold text-base">{v.label}</div>
      </div>
      <div className="text-[10px] uppercase tracking-wider mt-0.5 opacity-60">{v.sub}</div>
      <div className="text-xs mt-2 opacity-80 leading-snug">{v.blurb}</div>
    </button>
  );
};

const ExitBadge = ({ reason }) => {
  const cfg = {
    MOON: { bg: "bg-amber-100 text-amber-900", label: "🌙 MOON" },
    TRAIL: { bg: "bg-emerald-100 text-emerald-800", label: "TRAIL" },
    BE: { bg: "bg-blue-100 text-blue-800", label: "BE" },
    SL: { bg: "bg-red-100 text-red-800", label: "STOP" },
    TIME: { bg: "bg-neutral-100 text-neutral-700", label: "TIME" },
  };
  const c = cfg[reason] || cfg.TIME;
  return <span className={`${c.bg} px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wider whitespace-nowrap`}>{c.label}</span>;
};

const StatCard = ({ label, val, className = "", highlight }) => (
  <div className={`bg-white border ${highlight ? "border-mb-emerald" : "border-mb-border"} rounded-lg p-5`}>
    <div className="label-uppercase text-neutral-500">{label}</div>
    <div className={`mt-2 font-mono-data text-3xl font-extrabold ${className}`}>{val}</div>
  </div>
);

const Th = ({ children }) => <th className="px-4 py-3 text-left label-uppercase text-neutral-500 whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

// ───────────────── Public Proof: Steady vs Moonshot side-by-side ─────────────────
// Static snapshot of the latest walk-forward backtest results. Embedded so the
// Backtest page renders instantly on Day 0. The /api/backtest/proof endpoint
// still refreshes these numbers in the background if newer data is available.
// To refresh: curl $API/api/backtest/proof and paste the JSON here.
const STATIC_PROOF = {
  steady: {
    total_trades: 24,
    win_rate: 87.5,
    avg_return_pct: 2.26,
    avg_win_pct: 3.58,
    avg_loss_pct: -7.0,
    best_trade_pct: 7.25,
    worst_trade_pct: -7.0,
    hold_days: 15,
    take_profit_pct: 2.5,
    stop_loss_pct: 7.0,
    moon_target_pct: 12.0,
    trail_pct: 5.0,
    moon_hits: 4,
    trail_hits: 2,
    breakeven_hits: 14,
    sl_hits: 3,
    time_exits: 1,
    tp1_hit_count: 21,
    avg_days_held: 3.8,
    expectancy_pct: 2.26,
    variant: "baseline",
    variant_label: "Baseline (Strict A-Grade)",
    variant_tagline: "5-of-6 Pressure · 50/50 scale-out · TP1 +2.5% / Moon +12%",
  },
  moon: {
    total_trades: 59,
    win_rate: 59.3,
    avg_return_pct: 0.28,
    avg_win_pct: 6.84,
    avg_loss_pct: -9.3,
    best_trade_pct: 13.0,
    worst_trade_pct: -10.0,
    hold_days: 25,
    take_profit_pct: 6.0,
    stop_loss_pct: 10.0,
    moon_target_pct: 20.0,
    trail_pct: 10.0,
    moon_hits: 4,
    trail_hits: 0,
    breakeven_hits: 28,
    sl_hits: 22,
    time_exits: 5,
    tp1_hit_count: 34,
    avg_days_held: 9.6,
    expectancy_pct: 0.28,
    variant: "pre_breakout",
    variant_label: "Pre-Breakout Index",
    variant_tagline: "2-of-3 leading + Conviction Stack · TP1 +6% / Moon +20% / 25d hold",
  },
};

function PublicProofSection() {
  // Initialise with static snapshot — instant render, no spinner on first load.
  const [proof, setProof] = useState(STATIC_PROOF);

  useEffect(() => {
    let alive = true;
    // Silent background refresh — if API returns newer numbers, swap them in.
    api.get("/backtest/proof").then((r) => {
      if (!alive) return;
      const s = r.data?.steady, m = r.data?.moonshot;
      if (s && m && s.total_trades > 0) setProof({ steady: s, moon: m });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="public-proof">
      <ProofCard
        kind="steady"
        title="☕ Steady Tier"
        subtitle="Algorithm only · pre-desk-vetting"
        tagline="High win rate, modest wins. The cup of coffee insurance."
        stats={proof.steady}
        accent="emerald"
        liveLink={{ label: "Live track record (post-vetting): 78.6% →", to: "/track-record" }}
      />
      <ProofCard
        kind="moonshot"
        title="🚀 Moonshot Tier"
        subtitle="Algorithm only · pre-desk-vetting"
        tagline="We see the pressure before the breakout — when insiders, short squeeze pressure, and the tape all align."
        stats={proof.moon}
        accent="pink"
        liveLink={{ label: "Live track record (post-vetting): 88.9% →", to: "/track-record" }}
      />
    </div>
  );
}

function ProofCard({ kind, title, subtitle, tagline, stats, accent, liveLink }) {
  if (!stats || !stats.total_trades) {
    return (
      <div className={`rounded-lg bg-white border-2 ${accent === "pink" ? "border-pink-200" : "border-mb-emerald/20"} p-6`}>
        <div className="font-display font-bold text-lg">{title}</div>
        <div className="text-sm text-neutral-500 mt-2">No trades in window.</div>
      </div>
    );
  }
  const moonRate = Math.round((stats.moon_hits / stats.total_trades) * 100);
  const isPink = accent === "pink";
  return (
    <div
      className={`rounded-lg p-6 ${isPink ? "bg-gradient-to-br from-pink-500 to-pink-700 text-white" : "bg-mb-emerald text-white"}`}
      data-testid={`proof-card-${kind}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display font-semibold text-xl">{title}</div>
          <div className={`text-xs mt-0.5 font-mono-data tracking-wide ${isPink ? "text-white/70" : "text-white/70"}`}>{subtitle}</div>
        </div>
      </div>
      <p className="text-sm mt-3 text-white/90 leading-relaxed">{tagline}</p>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <ProofMetric label="Win Rate" val={`${stats.win_rate}%`} primary />
        <ProofMetric label="Per-trade exp." val={`${stats.expectancy_pct >= 0 ? "+" : ""}${stats.expectancy_pct}%`} primary />
        <ProofMetric label="Avg Win" val={`+${stats.avg_win_pct}%`} />
        <ProofMetric label="Best Trade" val={`+${stats.best_trade_pct}%`} highlight />
        <ProofMetric label="Moonshot rate" val={`${moonRate}%`} highlight={isPink} />
        <ProofMetric label="Trades" val={stats.total_trades} />
      </div>

      <div className="mt-4 text-[11px] text-white/70 font-mono-data leading-snug">
        12-month walk-forward · {stats.total_trades} entries · {stats.moon_hits} moonshots at +{stats.moon_target_pct}%
      </div>

      {liveLink && (
        <Link
          to={liveLink.to}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono-data font-bold tracking-wide rounded-md px-3 py-1.5 transition bg-white/15 hover:bg-white/25 text-white border border-white/30"
          data-testid={`proof-card-${kind}-live-link`}
        >
          {liveLink.label}
        </Link>
      )}
    </div>
  );
}

const ProofMetric = ({ label, val, primary, highlight }) => (
  <div className={`rounded-lg p-2.5 ${highlight ? "bg-white/20 ring-1 ring-white/40" : "bg-white/10"}`}>
    <div className="label-uppercase text-white/60 text-[9px]">{label}</div>
    <div className={`font-mono-data font-extrabold ${primary ? "text-2xl" : "text-base"} text-white`}>{val}</div>
  </div>
);

const ProofSkeleton = ({ kind }) => {
  const isMoon = kind === "moonshot";
  const title = isMoon ? "🚀 Moonshot Tier" : "☕ Steady Tier";
  const subtitle = isMoon ? "Pre-Breakout Index · Leading signals" : "Baseline · Strict A-Grade";
  return (
    <div
      className={`rounded-lg p-6 relative overflow-hidden ${
        isMoon
          ? "bg-gradient-to-br from-pink-500 to-pink-700"
          : "bg-mb-emerald"
      }`}
      data-testid={`proof-skeleton-${kind}`}
    >
      {/* Header — real, not placeholder */}
      <div className="font-display font-semibold text-xl text-white">{title}</div>
      <div className="text-xs mt-0.5 font-mono-data tracking-wide text-white/70">{subtitle}</div>

      {/* Shimmering metric placeholders */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-lg p-2.5 bg-white/10 overflow-hidden">
            <div className="h-2 w-16 bg-white/25 rounded animate-pulse" />
            <div className="h-5 w-12 mt-1.5 bg-white/30 rounded animate-pulse" style={{ animationDelay: `${i * 80}ms` }} />
          </div>
        ))}
      </div>

      <div className="mt-4 text-[11px] text-white/70 font-mono-data leading-snug flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin" />
        Running 12-month walk-forward simulation…
      </div>
    </div>
  );
};
